import { hexToHslChannels, pickForegroundChannels } from '@/shared/utils/color'

const BRAND_CSS_VARS = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--sidebar-accent',
  '--sidebar-accent-foreground',
  '--guest-background',
  '--sidebar',
  '--sidebar-border',
  '--sidebar-foreground'
] as const

type BrandCssVar = (typeof BRAND_CSS_VARS)[number]

let activeOverrides: Partial<Record<BrandCssVar, string>> | null = null

function applyOverridesToDom(): void {
  const root = document.documentElement
  if (!activeOverrides) return

  for (const [key, value] of Object.entries(activeOverrides) as [BrandCssVar, string][]) {
    root.style.setProperty(key, value)
  }
}

function clearOverridesFromDom(): void {
  const root = document.documentElement
  for (const key of BRAND_CSS_VARS) {
    root.style.removeProperty(key)
  }
}

export interface BrandThemeOptions {
  guestHex: string
  buttonHex?: string | null
  sidebarHex?: string | null
  sidebarTextHex?: string | null
}

let activeBrandOptions: BrandThemeOptions | null = null

function applyBrandThemeInternal(options: BrandThemeOptions): void {
  const guestHexVal = options.guestHex || options.buttonHex || '#7C3AED'
  const guestChannels = hexToHslChannels(guestHexVal)
  if (!guestChannels) return

  const buttonHexVal = options.buttonHex || options.guestHex || '#7C3AED'
  let btnChannels = hexToHslChannels(buttonHexVal)

  if (btnChannels) {
    const isDarkMode = document.documentElement.classList.contains('dark')

    // Programmatically scale lightness channel of the brand color to ensure readability
    const parts = btnChannels.match(/^(\d+)\s+(\d+)%\s+(\d+)%$/)
    if (parts) {
      const h = parseInt(parts[1])
      const s = parseInt(parts[2])
      let l = parseInt(parts[3])

      if (isDarkMode) {
        // In dark mode, ensure color is bright enough against dark background
        if (l < 45) {
          l = 45
        }
      } else {
        // In light mode, ensure color is dark enough to read on white/light background
        if (l > 75) {
          l = 75
        }
      }
      btnChannels = `${h} ${s}% ${l}%`
    }
  }

  const btnForeground = pickForegroundChannels(buttonHexVal)
  const resolvedBtnChannels = btnChannels || guestChannels

  const overrides: Partial<Record<BrandCssVar, string>> = {
    '--primary': resolvedBtnChannels,
    '--primary-foreground': btnForeground,
    '--ring': resolvedBtnChannels,
    '--sidebar-accent': resolvedBtnChannels,
    '--sidebar-accent-foreground': btnForeground,
    '--guest-background': guestChannels
  }

  if (options.sidebarHex) {
    const sidebarChannels = hexToHslChannels(options.sidebarHex)
    if (sidebarChannels) {
      overrides['--sidebar'] = sidebarChannels
      overrides['--sidebar-border'] = sidebarChannels
    }
  }

  if (options.sidebarTextHex) {
    const sidebarTextChannels = hexToHslChannels(options.sidebarTextHex)
    if (sidebarTextChannels) {
      overrides['--sidebar-foreground'] = sidebarTextChannels
    }
  }

  activeOverrides = overrides
  applyOverridesToDom()
}

/** Applies reseller brand color to semantic CSS variables (runtime white-label). */
export function applyBrandTheme(options: BrandThemeOptions): void {
  activeBrandOptions = options
  applyBrandThemeInternal(options)
}

/** Removes runtime brand overrides; SCSS defaults apply again. */
export function resetBrandTheme(): void {
  activeBrandOptions = null
  activeOverrides = null
  clearOverridesFromDom()
}

/** Re-applies stored overrides after theme mode changes (.dark toggles base tokens). */
export function reapplyBrandTheme(): void {
  if (activeBrandOptions) {
    applyBrandThemeInternal(activeBrandOptions)
  }
}
